import { useEffect } from 'react';
import { Keyboard } from '@capacitor/keyboard';
import { isNativeApp } from '../utils/platform';

// Lock body scroll while `active` (an overlay/drawer is open). iOS Safari /
// WKWebView ignore body{overflow:hidden} for touch scrolling, so pin the body
// with position:fixed (snapshotting + restoring scrollY) — the reliable way to
// stop the page scrolling behind the overlay. On native we also disable
// WKWebView's scroll-to-focused-input so typing in a field doesn't drag the
// page behind the sheet. Mirrors the proven block in ServiceBookingDrawer so
// the two overlays can't drift.
export default function useScrollLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    const scrollY = window.scrollY;
    const { body } = document;
    // Snapshot only the props we touch — never the whole style attribute, so we
    // don't wipe inline styles another component set on <body> while we're open.
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      // html has scroll-behavior:smooth (index.css); restoring would animate a
      // jarring slide as the overlay closes. Force an instant jump, then restore.
      const html = document.documentElement;
      const prevBehavior = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      window.scrollTo(0, scrollY);
      html.style.scrollBehavior = prevBehavior;
    };
  }, [active]);

  useEffect(() => {
    if (!active || !isNativeApp) return undefined;
    Keyboard.setScroll({ isDisabled: true }).catch(() => {});
    return () => { Keyboard.setScroll({ isDisabled: false }).catch(() => {}); };
  }, [active]);
}
